import { BrokerInfo, GetBrokerBranchInfoDocument, GetBrokerInfoDocument } from 'views/utils/graphql/graphql';

import { useFormik } from 'formik';
import { ModalStep } from '../hooks';

import { Dispatch, SetStateAction } from 'react';
import { useClient } from 'urql';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';

enum TargetType {
  none = 0,
  bank = 1,
  branch = 2,
}

enum SearchKeyType {
  none = '',
  kanaChar = 'selectedKana',
  kana = 'kanaInput',
  code = 'code',
}

export type SearchType = {
  target: TargetType;
  kind: SearchKeyType; //検索のキータイプ
  kanaChar: string;
  kana: string;
  code: string;
};

const isKanaOnly = (input: string): boolean => {
  const kanaRegex = /^[\u30A0-\u30FF]+$/;
  return kanaRegex.test(input);
};

const isThreeDigitNumber = (input: string): boolean => /^\d{3}$/.test(input);
const isFourDigitNumber = (input: string): boolean => /^\d{4}$/.test(input);

const largeChengeKana = (kanaInput: string): string => {
  const kanaList: Record<string, string> = {
    'ァ': 'ア', 'ィ': 'イ', 'ゥ': 'ウ', 'ェ': 'エ', 'ォ': 'オ',
    'ッ': 'ツ', 'ャ': 'ヤ', 'ュ': 'ユ', 'ョ': 'ヨ',
    'ヮ': 'ワ', 'ヵ': 'カ', 'ヶ': 'ケ',
  };
  
  return kanaInput.replace(
    /[ァィゥェォッャュョヮヵヶ]/g,
    (char) => kanaList[char] || char
  );
};

export const useSearchBank = (
  modalStep: ModalStep,
  setModalStep: Dispatch<SetStateAction<ModalStep>>,
  setBankList: Dispatch<SetStateAction<string[]>>,
) => {
  const client = useClient();
  const { showLoading, hideLoading } = useGlobalLoadingHandler();

  const validate = (values: SearchType) => {
    const errors: Partial<Record<keyof SearchType, string>> = {};

    if (values.kind === SearchKeyType.kana && !isKanaOnly(values.kana)) {
      errors.kana = 'カタカナで入力してください';
    } else if (values.kind === SearchKeyType.code && !isFourDigitNumber(values.code)) {
      errors.code = '4桁の数字で入力してください';
    }

    return errors;
  };

  const initialValues: SearchType = {
    target: TargetType.bank,
    kind: SearchKeyType.none,
    kanaChar: '',
    kana: '',
    code: '',
  };

  const formik = useFormik<SearchType>({
    initialValues,
    async onSubmit(input, { setErrors }) {
      const errorMsg = '該当するデータがありません';

      let target = '';
      let code = null;
      const error: Partial<Record<keyof SearchType, string>> = {};
      switch (input.kind) {
        case SearchKeyType.kana:
          target = largeChengeKana(input.kana);
          error.kana = errorMsg;
          break;
        case SearchKeyType.kanaChar:
          target = input.kanaChar;
          error.kanaChar = errorMsg;
          break;
        case SearchKeyType.code:
          code = input.code;
          error.code = errorMsg;
          break;
      }

      showLoading();
      const ret = await client.query(
        GetBrokerInfoDocument,
        { brokerName: target, brokerCode: code },
        { requestPolicy: 'network-only' },
      );
      hideLoading();

      const list = (ret.data?.GetBrokerInfo?.Data as Array<BrokerInfo>) ?? [];
      if (list.length === 0) {
        setErrors(error);
        return;
      }

      const nameList = list.map((x) => x.BrokerName!);

      setBankList(nameList);
      setModalStep(ModalStep.SelectBank);
    },
    // validationSchema: validationSchema,
    validate: validate,
    validateOnChange: false,
    validateOnBlur: false,
  });

  //props
  const selectedKanaProps = {
    value: formik.values.kanaChar,
    onChange: async (value: string) => {
      formik.setFieldValue('kind', SearchKeyType.kanaChar);
      await formik.setFieldValue('kanaChar', value);
      formik.submitForm();
    },
    error: formik.errors.kanaChar,
    title: '1 : 50音で検索',
  };

  const inputKanaProps = {
    inputTemplete: {
      value: formik.values.kana,
      type: 'text',
      onChange: async (value: string) => {
        await formik.setFieldValue('kana', value);
      },
      error: formik.errors.kana,
    },
    onSearch: () => {
      formik.setFieldValue('kind', SearchKeyType.kana);
    },
    title: '2：金融機関名のカナで検索',
    comment: '',
    example: '例）ホクリク',
  };

  const inputCodeProps = {
    inputTemplete: {
      value: formik.values.code,
      type: 'text',
      onChange: async (value: string) => {
        await formik.setFieldValue('code', value);
      },
      error: formik.errors.code,
    },
    onSearch: () => {
      formik.setFieldValue('kind', SearchKeyType.code);
    },
    title: '3：金融機関コードで検索',
    comment: '',
    example: '',
  };

  return {
    formik,
    selectedKanaProps,
    inputKanaProps,
    inputCodeProps,
  };
};

export const useSearchBranch = (
  bankName: string,
  modalStep: ModalStep,
  setModalStep: Dispatch<SetStateAction<ModalStep>>,
  setBranchList: Dispatch<SetStateAction<string[]>>,
) => {
  const client = useClient();
  const { showLoading, hideLoading } = useGlobalLoadingHandler();

  const validate = (values: SearchType) => {
    const errors: Partial<Record<keyof SearchType, string>> = {};

    if (values.kind === SearchKeyType.kana && !isKanaOnly(values.kana)) {
      errors.kana = 'カタカナで入力してください';
    } else if (values.kind === SearchKeyType.code && !isThreeDigitNumber(values.code)) {
      errors.code = '3桁の数字で入力してください';
    }

    return errors;
  };

  const initialValues: SearchType = {
    target: TargetType.branch,
    kind: SearchKeyType.none,
    kanaChar: '',
    kana: '',
    code: '',
  };

  const formik = useFormik<SearchType>({
    initialValues,
    async onSubmit(input, { setErrors }) {
      const errorMsg = '該当するデータがありません';

      let target = '';
      let code = null;
      const error: Partial<Record<keyof SearchType, string>> = {};
      switch (input.kind) {
        case SearchKeyType.kana:
          target = largeChengeKana(input.kana);
          error.kana = errorMsg;
          break;
        case SearchKeyType.kanaChar:
          target = input.kanaChar;
          error.kanaChar = errorMsg;
          break;
        case SearchKeyType.code:
          code = input.code;
          error.code = errorMsg;
          break;
      }

      showLoading();
      const ret = await client.query(
        GetBrokerBranchInfoDocument,
        { brokerName: bankName, branchName: target, branchCode: code },
        { requestPolicy: 'network-only' },
      );
      hideLoading();

      const list = (ret.data?.GetBrokerBranchInfo?.Data as Array<BrokerInfo>) ?? [];
      if (list.length === 0) {
        setErrors(error);
        return;
      }

      const nameList = list.map((x) => x.BranchName!);

      setBranchList(nameList);
      setModalStep(ModalStep.SelectBranch);
    },
    // validationSchema: validationSchema,
    validate: validate,
    validateOnChange: false,
    validateOnBlur: false,
  });

  //props
  const selectedKanaProps = {
    value: formik.values.kanaChar,
    onChange: async (value: string) => {
      formik.setFieldValue('kind', SearchKeyType.kanaChar);
      await formik.setFieldValue('kanaChar', value);
      formik.submitForm();
    },
    error: formik.errors.kanaChar,
    title: '1：支店名を50音で検索',
  };

  const inputKanaProps = {
    inputTemplete: {
      value: formik.values.kana,
      type: 'text',
      onChange: async (value: string) => {
        await formik.setFieldValue('kana', value);
      },
      error: formik.errors.kana,
    },
    onSearch: () => {
      formik.setFieldValue('kind', SearchKeyType.kana);
    },
    title: '2：支店名のカナで検索',
    comment: '※アルファベットの支店名の場合は英字で検索してください。',
    example: '例）シンジュク',
  };

  const inputCodeProps = {
    inputTemplete: {
      value: formik.values.code,
      type: 'text',
      onChange: async (value: string) => {
        await formik.setFieldValue('code', value);
      },
      error: formik.errors.code,
    },
    onSearch: () => {
      formik.setFieldValue('kind', SearchKeyType.code);
    },
    title: '3：支店名コードで検索',
    comment: '',
    example: '',
  };

  return {
    formik,
    selectedKanaProps,
    inputKanaProps,
    inputCodeProps,
  };
};
